import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, ShoppingBagIcon, UserIcon, PhoneIcon, MailIcon, MapPinIcon, CheckIcon, XIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
// Mock merchant data
const merchantsData = [{
  id: 1,
  name: 'Bismillah Traders',
  status: 'active'
}, {
  id: 2,
  name: 'Karachi Super Mart',
  status: 'active'
}, {
  id: 3,
  name: 'Chase Value Center Gulshan e Iqbal',
  status: 'active'
}, {
  id: 4,
  name: 'Orient Online Shop',
  status: 'active'
}, {
  id: 5,
  name: 'Cafe Khaas',
  status: 'active'
}, {
  id: 6,
  name: 'Zeen Textile',
  status: 'active'
}, {
  id: 7,
  name: 'Islamabad Metro',
  status: 'active'
}, {
  id: 8,
  name: 'Ginsoy Lahore',
  status: 'active'
}];
// Mock tills data
const tillsData = [{
  id: 'TILL001',
  merchantId: 1,
  merchantName: 'Bismillah Traders',
  paymentMode: 'POS',
  userName: 'Ahmed Khan',
  userId: 'AK001',
  mobileNumber: '+92 300 1234567',
  email: 'ahmed@bismillahtraders.pk',
  location: 'Shop #5, Main Market, Islamabad',
  status: 'active'
}, {
  id: 'TILL009',
  merchantId: 1,
  merchantName: 'Bismillah Traders',
  paymentMode: 'QRC',
  userName: 'Faisal Malik',
  userId: 'FM009',
  mobileNumber: '+92 300 9876543',
  email: 'faisal@bismillahtraders.pk',
  location: 'Branch #2, Blue Area, Islamabad',
  status: 'active'
}, {
  id: 'TILL002',
  merchantId: 2,
  merchantName: 'Karachi Super Mart',
  paymentMode: 'QRC',
  userName: 'Fatima Ali',
  userId: 'FA002',
  mobileNumber: '+92 321 9876543',
  email: 'fatima@karachisupermart.pk',
  location: 'Branch #2, Clifton, Karachi',
  status: 'active'
}
// More tills data can be added here
];
interface Merchant {
  id: number;
  name: string;
  status: string;
}
interface Till {
  id: string;
  merchantId: number;
  merchantName: string;
  paymentMode: string;
  userName: string;
  userId: string;
  mobileNumber: string;
  email: string;
  location: string;
  status: string;
}
const TillForm: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    tillName: '',
    tillLocation: '',
    merchantId: '',
    paymentMode: '',
    deviceSerialNumber: '',
    status: 'active'
  });
  // State for the form
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [merchantTills, setMerchantTills] = useState<Till[]>([]);
  const [selectedTill, setSelectedTill] = useState<Till | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Load data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const till = tillsData.find(t => t.id === id);
      if (till) {
        const merchant = merchantsData.find(m => m.id === till.merchantId);
        setSelectedMerchant(merchant || null);
        setSelectedTill(till);
        setFormData({
          tillName: till.userName,
          tillLocation: till.location,
          merchantId: till.merchantId,
          paymentMode: till.paymentMode,
          deviceSerialNumber: '',
          status: till.status
        });
        // Load merchant tills
        const tills = tillsData.filter(t => t.merchantId === till.merchantId);
        setMerchantTills(tills);
      }
    }
  }, [isEditMode, id]);
  // Handle merchant selection
  const handleMerchantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const merchantId = parseInt(e.target.value);
    if (merchantId) {
      const merchant = merchantsData.find(m => m.id === merchantId);
      setSelectedMerchant(merchant || null);
      // Load tills for selected merchant
      const tills = tillsData.filter(t => t.merchantId === merchantId);
      setMerchantTills(tills);
      // Reset selected till and form data
      setSelectedTill(null);
      setFormData({
        tillName: '',
        tillLocation: '',
        merchantId: '',
        paymentMode: '',
        deviceSerialNumber: '',
        status: 'active'
      });
    } else {
      setSelectedMerchant(null);
      setMerchantTills([]);
      setSelectedTill(null);
    }
  };
  // Handle till selection
  const handleTillSelect = (till: Till) => {
    setSelectedTill(till);
    setFormData({
      tillName: till.userName,
      tillLocation: till.location,
      merchantId: till.merchantId,
      paymentMode: till.paymentMode,
      deviceSerialNumber: '',
      status: till.status
    });
  };
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Validate form
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.tillName) newErrors.tillName = 'Till name is required';
    if (!formData.tillLocation) newErrors.tillLocation = 'Till location is required';
    if (!formData.merchantId) newErrors.merchantId = 'Merchant is required';
    if (!formData.paymentMode) newErrors.paymentMode = 'Payment mode is required';else if (formData.paymentMode === 'POS' || formData.paymentMode === 'MobilePOS') {
      if (!formData.deviceSerialNumber) newErrors.deviceSerialNumber = 'Device serial number is required';
    }
    if (!formData.status) newErrors.status = 'Status is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMerchant) {
      setErrors({
        merchant: 'Please select a merchant'
      });
      return;
    }
    if (validateForm()) {
      setIsSubmitting(true);
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        // Reset form after success
        setTimeout(() => {
          if (!isEditMode) {
            setFormData({
              tillName: '',
              tillLocation: '',
              merchantId: '',
              paymentMode: '',
              deviceSerialNumber: '',
              status: 'active'
            });
            setSelectedTill(null);
          }
          setSubmitSuccess(false);
        }, 3000);
      }, 1500);
    }
  };
  // Handle cancel
  const handleCancel = () => {
    navigate('/tills');
  };
  // Get payment mode icon
  const getPaymentModeIcon = (mode: string) => {
    switch (mode) {
      case 'POS':
        return <CreditCardIcon className="h-5 w-5 text-blue-500" data-id="element-5560" />;
      case 'QRC':
        return <QrCodeIcon className="h-5 w-5 text-purple-500" data-id="element-5561" />;
      case 'SoftPOS':
        return <SmartphoneIcon className="h-5 w-5 text-green-500" data-id="element-5562" />;
      case 'MobilePOS':
        return <ShoppingBagIcon className="h-5 w-5 text-orange-500" data-id="element-5563" />;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-5564">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-5565">
        <div className="flex items-center" data-id="element-5566">
          <button onClick={() => navigate('/tills')} className="mr-3 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" data-id="element-5567">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" data-id="element-5568" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5569">
            {isEditMode ? 'Edit Till' : 'Add New Till'}
          </h1>
        </div>
      </div>
      <Card data-id="element-5570">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-5571">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5572">
            {/* Till Name */}
            <div data-id="element-5573">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5574">
                Till Name <span className="text-red-500" data-id="element-5575">*</span>
              </label>
              <input type="text" name="tillName" value={formData.tillName} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter till name" required data-id="element-5576" />
            </div>
            {/* Till Location */}
            <div data-id="element-5577">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5578">
                Till Location <span className="text-red-500" data-id="element-5579">*</span>
              </label>
              <input type="text" name="tillLocation" value={formData.tillLocation} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter till location" required data-id="element-5580" />
            </div>
            {/* Merchant */}
            <div data-id="element-5581">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5582">
                Merchant <span className="text-red-500" data-id="element-5583">*</span>
              </label>
              <select name="merchantId" value={formData.merchantId} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5584">
                <option value="" data-id="element-5585">Select Merchant</option>
                {merchantsData.map(merchant => <option key={merchant.id} value={merchant.id} data-id="element-5586">
                    {merchant.name} ({merchant.id})
                  </option>)}
              </select>
            </div>
            {/* Payment Mode */}
            <div data-id="element-5587">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5588">
                Payment Mode <span className="text-red-500" data-id="element-5589">*</span>
              </label>
              <select name="paymentMode" value={formData.paymentMode} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5590">
                <option value="" data-id="element-5591">Select Payment Mode</option>
                <option value="QR" data-id="element-5592">QR</option>
                <option value="POS" data-id="element-5593">POS</option>
                <option value="MobilePOS" data-id="element-5594">Mobile POS</option>
                <option value="Online" data-id="element-5595">Online</option>
              </select>
            </div>
            {/* Device Serial Number - Show only for POS or MobilePOS */}
            {(formData.paymentMode === 'POS' || formData.paymentMode === 'MobilePOS') && <div className="md:col-span-2" data-id="element-5596">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5597">
                  Device Serial Number{' '}
                  <span className="text-gray-500 text-xs" data-id="element-5598">(Optional)</span>
                </label>
                <input type="text" name="deviceSerialNumber" value={formData.deviceSerialNumber} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" placeholder="Enter device serial number" data-id="element-5599" />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-5600">
                  Enter the serial number of the POS device if available
                </p>
              </div>}
            {/* Status */}
            <div data-id="element-5601">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5602">
                Status <span className="text-red-500" data-id="element-5603">*</span>
              </label>
              <select name="status" value={formData.status} onChange={handleInputChange} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" required data-id="element-5604">
                <option value="active" data-id="element-5605">Active</option>
                <option value="inactive" data-id="element-5606">Inactive</option>
              </select>
            </div>
          </div>

          {/* Existing Tills for Selected Merchant */}
          {selectedMerchant && merchantTills.length > 0 && !isEditMode && <div data-id="element-5607">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-5608">
                Existing Tills for {selectedMerchant.name}
              </h3>
              <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3" data-id="element-5609">
                <div className="max-h-48 overflow-y-auto" data-id="element-5610">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5611">
                    <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-5612">
                      <tr data-id="element-5613">
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5614">
                          Till ID
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5615">
                          Payment Mode
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5616">
                          User
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5617">
                          Location
                        </th>
                        <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5618">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5619">
                      {merchantTills.map(till => <tr key={till.id} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedTill?.id === till.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} onClick={() => handleTillSelect(till)} data-id="element-5620">
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5621">
                            {till.id}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5622">
                            <div className="flex items-center" data-id="element-5623">
                              {getPaymentModeIcon(till.paymentMode)}
                              <span className="ml-1" data-id="element-5624">{till.paymentMode}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5625">
                            {till.userName}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5626">
                            {till.location.length > 25 ? `${till.location.substring(0, 25)}...` : till.location}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-5627">
                            {till.status === 'active' ? <StatusBadge status="success" text="Active" data-id="element-5628" /> : <StatusBadge status="danger" text="Inactive" data-id="element-5629" />}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
                {selectedTill && <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md" data-id="element-5630">
                    <div className="flex items-center" data-id="element-5631">
                      <InfoIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" data-id="element-5632" />
                      <p className="text-sm text-blue-700 dark:text-blue-300" data-id="element-5633">
                        Till{' '}
                        <span className="font-medium" data-id="element-5634">{selectedTill.id}</span>{' '}
                        is selected for editing.
                      </p>
                    </div>
                  </div>}
              </div>
            </div>}

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-5635">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-5636">
              {isEditMode || selectedTill ? 'Edit Till Details' : 'New Till Details'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5637">
              {/* Payment Mode */}
              <div data-id="element-5638">
                <label htmlFor="paymentMode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5639">
                  Payment Mode
                </label>
                <select id="paymentMode" name="paymentMode" className={`w-full rounded-md border ${errors.paymentMode ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} value={formData.paymentMode} onChange={handleInputChange} data-id="element-5640">
                  <option value="" data-id="element-5641">Select payment mode</option>
                  <option value="POS" data-id="element-5642">POS</option>
                  <option value="QRC" data-id="element-5643">QRC</option>
                  <option value="SoftPOS" data-id="element-5644">SoftPOS</option>
                  <option value="MobilePOS" data-id="element-5645">MobilePOS</option>
                </select>
                {errors.paymentMode && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5646">
                    {errors.paymentMode}
                  </p>}
              </div>

              {/* User Name */}
              <div data-id="element-5647">
                <label htmlFor="userName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5648">
                  Till User Name
                </label>
                <div className="relative" data-id="element-5649">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5650">
                    <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-5651" />
                  </div>
                  <input type="text" id="userName" name="userName" className={`w-full pl-10 rounded-md border ${errors.userName ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter till user's name" value={formData.userName} onChange={handleInputChange} data-id="element-5652" />
                </div>
                {errors.userName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5653">
                    {errors.userName}
                  </p>}
              </div>

              {/* User ID */}
              <div data-id="element-5654">
                <label htmlFor="userId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5655">
                  Till User ID
                </label>
                <input type="text" id="userId" name="userId" className={`w-full rounded-md border ${errors.userId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter till user's ID" value={formData.userId} onChange={handleInputChange} data-id="element-5656" />
                {errors.userId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5657">
                    {errors.userId}
                  </p>}
              </div>

              {/* Mobile Number */}
              <div data-id="element-5658">
                <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5659">
                  Mobile Number
                </label>
                <div className="relative" data-id="element-5660">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5661">
                    <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-5662" />
                  </div>
                  <input type="text" id="mobileNumber" name="mobileNumber" className={`w-full pl-10 rounded-md border ${errors.mobileNumber ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="+92 300 1234567" value={formData.mobileNumber} onChange={handleInputChange} data-id="element-5663" />
                </div>
                {errors.mobileNumber && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5664">
                    {errors.mobileNumber}
                  </p>}
              </div>

              {/* Email */}
              <div data-id="element-5665">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5666">
                  Email
                </label>
                <div className="relative" data-id="element-5667">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5668">
                    <MailIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-5669" />
                  </div>
                  <input type="email" id="email" name="email" className={`w-full pl-10 rounded-md border ${errors.email ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="user@example.com" value={formData.email} onChange={handleInputChange} data-id="element-5670" />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5671">
                    {errors.email}
                  </p>}
              </div>

              {/* Location */}
              <div className="md:col-span-2" data-id="element-5672">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-5673">
                  Till Location (Address)
                </label>
                <div className="relative" data-id="element-5674">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5675">
                    <MapPinIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" data-id="element-5676" />
                  </div>
                  <input type="text" id="location" name="location" className={`w-full pl-10 rounded-md border ${errors.location ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} placeholder="Enter physical address of the till" value={formData.location} onChange={handleInputChange} data-id="element-5677" />
                </div>
                {errors.location && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-5678">
                    {errors.location}
                  </p>}
              </div>
            </div>
          </div>

          {/* Notice */}
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3" data-id="element-5679">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 flex items-start" data-id="element-5680">
              <AlertTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-5681" />
              <span data-id="element-5682">
                <strong data-id="element-5683">Note:</strong> This request will be submitted for
                approval by a Checker before it is processed. You will be
                notified once the request is approved or rejected.
              </span>
            </p>
          </div>

          {/* Success Message */}
          {submitSuccess && <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded p-3" data-id="element-5684">
              <p className="text-sm text-green-800 dark:text-green-300 flex items-start" data-id="element-5685">
                <CheckIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-5686" />
                <span data-id="element-5687">
                  <strong data-id="element-5688">Success!</strong> Your request has been submitted for
                  approval.
                </span>
              </p>
            </div>}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-5689">
            <button type="button" onClick={handleCancel} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-5690">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting || submitSuccess} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubmitting || submitSuccess ? 'bg-blue-400 dark:bg-blue-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-5691">
              {isSubmitting ? 'Submitting...' : isEditMode ? 'Update Till' : 'Add Till'}
            </button>
          </div>
        </form>
      </Card>
    </div>;
};
export default TillForm;